import { ApiError } from "@carepass/api-client";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminListSearch, pathForAdminEntityScreen } from "../../app/routes";
import { failure } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { NotificationView } from "./components/NotificationView";
import { RegisteredTemplates } from "./components/RegisteredTemplates";
import type { NotificationQueryState } from "./components/NotificationView";
import {
  useNotificationCampaigns,
  useNotificationDetail,
  useNotificationList,
  useNotificationMutation,
  useNotificationPolicies,
  useNotificationTemplates,
} from "./queries";
import type { NotificationMutation } from "./queries";
import {
  parseNotificationDate,
  parseNotificationStatus,
  parseNotificationTab,
  parsePositiveId,
} from "./schema";
import type { NotificationSearchValues, NotificationTab } from "./schema";

function notificationSearchParams(tab: NotificationTab, page: number, size: number, filters: NotificationSearchValues) {
  const params = new URLSearchParams({ tab, page: String(page), size: String(size) });
  if (filters.deliveryStatus) params.set("deliveryStatus", filters.deliveryStatus);
  if (filters.memberId) params.set("memberId", filters.memberId);
  if (filters.campaignId) params.set("campaignId", filters.campaignId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  return params;
}

function stateOf(query: { isPending: boolean; isError: boolean }): NotificationQueryState {
  if (query.isPending) return "loading";
  if (query.isError) return "error";
  return "ready";
}

function unresolvedRetryId(commands: Readonly<Record<string, { readonly startedAt: string }>>): number | undefined {
  const item = Object.entries(commands)
    .filter(([key]) => key.startsWith("notification-retry:"))
    .sort(([, left], [, right]) => right.startedAt.localeCompare(left.startedAt))[0];
  if (!item) return undefined;
  const parsed = Number(item[0].slice("notification-retry:".length));
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function NotificationPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useAdminSession();
  const commands = useAdminUiStore((state) => state.unresolvedCommands);
  const markCommandUnresolved = useAdminUiStore((state) => state.markCommandUnresolved);
  const resolveCommand = useAdminUiStore((state) => state.resolveCommand);
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const pendingRetryId = useMemo(() => unresolvedRetryId(commands), [commands]);
  const tab = parseNotificationTab(searchParams.get("tab"));
  const list = parseAdminListSearch(searchParams, { page: 0, size: 20 });
  const from = parseNotificationDate(searchParams.get("from"));
  const parsedTo = parseNotificationDate(searchParams.get("to"));
  const filters = {
    deliveryStatus: parseNotificationStatus(searchParams.get("deliveryStatus")),
    memberId: parsePositiveId(searchParams.get("memberId")),
    campaignId: parsePositiveId(searchParams.get("campaignId")),
    from,
    to: from && parsedTo && from > parsedTo ? "" : parsedTo,
  } satisfies NotificationSearchValues;
  const history = useNotificationList({ ...filters, page: list.page, size: list.size }, tab === "history");
  const templates = useNotificationTemplates(["templates", "policies", "campaigns"].includes(tab));
  const policies = useNotificationPolicies(tab === "policies");
  const campaigns = useNotificationCampaigns(tab === "campaigns");
  const [selectedId, setSelectedId] = useState<number | undefined>(() => pendingRetryId);
  const detail = useNotificationDetail(selectedId);
  const mutation = useNotificationMutation();
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const authorities = session.authSession?.authorities ?? [];
  const lastPage = Math.max(0, Math.ceil((history.data?.totalCount ?? 0) / list.size) - 1);
  const navigation = useAdminNavigation({
    contextKey: "notifications",
    defaultListPath: "/notifications?tab=history&page=0&size=20",
    originLabel: "알림 발송 이력",
  });
  const restorationNotice = useRestoreAdminListViewport("notifications", tab !== "history" || Boolean(history.data));

  useEffect(() => {
    const canonical = notificationSearchParams(tab, list.page, list.size, filters);
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true });
  }, [filters.campaignId, filters.deliveryStatus, filters.from, filters.memberId, filters.to, list.page, list.size, searchParams, setSearchParams, tab]);

  useEffect(() => {
    if (pendingRetryId) setSelectedId(pendingRetryId);
  }, [pendingRetryId]);

  useEffect(() => {
    if (!history.data || list.page <= lastPage) return;
    setSelectedId(pendingRetryId);
    const params = notificationSearchParams(tab, lastPage, list.size, filters);
    rememberListContext("notifications", {
      path: `${location.pathname}?${params.toString()}`,
      page: lastPage,
      filters: { ...filters, tab, size: String(list.size) },
    }, true);
    setSearchParams(params, { replace: true });
  }, [filters.campaignId, filters.deliveryStatus, filters.from, filters.memberId, filters.to, history.data, lastPage, list.page, list.size, location.pathname, pendingRetryId, rememberListContext, setSearchParams, tab]);

  const remember = (nextTab: NotificationTab, page: number, nextFilters: NotificationSearchValues) => {
    const params = notificationSearchParams(nextTab, page, list.size, nextFilters);
    rememberListContext("notifications", {
      path: `${location.pathname}?${params.toString()}`,
      page,
      filters: { ...nextFilters, tab: nextTab, size: String(list.size) },
    });
    setSearchParams(params);
    navigation.focusListResults();
  };

  const onMutation = async (input: NotificationMutation) => {
    setNotice("");
    setRecoveryError("");
    const retryKey = input.kind === "retry" ? `notification-retry:${input.id}` : undefined;
    if (input.kind === "retry") {
      markCommandUnresolved(`notification-retry:${input.id}`, {
        operation: "POST /api/v1/admin/notifications/{notificationId}/retry",
        targetId: String(input.id),
        startedAt: new Date().toISOString(),
      });
    }
    try {
      return await mutation.mutateAsync(input);
    } catch (cause) {
      if (retryKey && !failure(cause).reconcile) resolveCommand(retryKey);
      throw cause;
    }
  };

  const onMutationComplete = async (input: NotificationMutation, result: unknown) => {
    if (input.kind === "retry") {
      setSelectedId(input.id);
      const [latestDetail, latestList] = await Promise.all([detail.refetch(), history.refetch()]);
      if (latestDetail.error) {
        setRecoveryError("재시도 요청은 접수됐지만 최신 상태를 불러오지 못했습니다. 다시 전송하지 말고 상태를 조회해 주세요.");
        return;
      }
      resolveCommand(`notification-retry:${input.id}`);
      if (latestList.error) setRecoveryError("선택 알림의 상태는 확인했지만 발송 이력 목록을 갱신하지 못했습니다.");
      setNotice(`${latestDetail.data?.notification.notificationNo ?? `알림 ID ${input.id}`}의 재시도 요청 상태를 확인했습니다.`);
      return;
    }
    if (input.kind === "single") {
      await history.refetch();
      const created = result as Schemas["NotificationView"];
      setSelectedId(created.id);
      setNotice(`${created.notificationNo} 예약을 접수했습니다. 실제 발송 결과는 발송 이력에서 확인해 주세요.`);
      return;
    }
    if (input.kind.startsWith("template")) {
      const refreshed = await templates.refetch();
      if (refreshed.error) setRecoveryError("템플릿 저장 응답은 받았지만 목록을 갱신하지 못했습니다.");
      const saved = result as Schemas["NotificationTemplateView"];
      setNotice(`${saved.templateCode} · 서버 ID ${saved.id} 템플릿을 저장했습니다.`);
      return;
    }
    if (input.kind.startsWith("policy")) {
      const refreshed = await policies.refetch();
      if (refreshed.error) setRecoveryError("정책 저장 응답은 받았지만 목록을 갱신하지 못했습니다.");
      const saved = result as Schemas["NotificationPolicyView"];
      setNotice(`${saved.policyGroupCode} · 서버 ID ${saved.id} 정책을 저장했습니다.`);
      return;
    }
    const [campaignResult, listResult] = await Promise.all([campaigns.refetch(), history.refetch()]);
    if (campaignResult.error || listResult.error) setRecoveryError("캠페인 예약 응답은 받았지만 최신 집계 또는 발송 이력을 모두 갱신하지 못했습니다.");
    const created = result as Schemas["NotificationCampaignView"];
    setNotice(`${created.campaignNo} 예약을 접수했습니다. 대상 ${created.targetCount.toLocaleString("ko-KR")}명이며 발송 완료 여부는 상태와 집계로 확인해 주세요.`);
  };

  const reconcileRetry = async () => {
    if (!pendingRetryId) return;
    setRecoveryError("");
    setSelectedId(pendingRetryId);
    const [latestDetail, latestList] = await Promise.all([detail.refetch(), history.refetch()]);
    if (latestDetail.error) {
      setRecoveryError(failure(latestDetail.error).message);
      throw latestDetail.error;
    }
    resolveCommand(`notification-retry:${pendingRetryId}`);
    if (latestList.error) setRecoveryError("알림 상세는 확인했지만 발송 이력 목록을 갱신하지 못했습니다.");
    setNotice(`${latestDetail.data?.notification.notificationNo ?? `알림 ID ${pendingRetryId}`}의 최신 상태를 확인했습니다.`);
  };

  const retryQuery = (scope: "history" | "detail" | "templates" | "policies" | "campaigns") => {
    const query = { history, detail, templates, policies, campaigns }[scope];
    void query.refetch();
  };

  const detailState = !selectedId ? "idle"
    : detail.isPending ? "loading"
      : detail.error instanceof ApiError && detail.error.kind === "forbidden" ? "denied"
        : detail.error instanceof ApiError && detail.error.kind === "not-found" ? "not-found"
          : detail.isError ? "error" : "ready";

  return <NotificationView
    mode="list"
    tab={tab}
    filters={filters}
    notifications={history.data}
    notificationState={stateOf(history)}
    selectedId={selectedId}
    detail={detail.data}
    detailState={detailState}
    onPrepareSingle={() => { void templates.refetch(); }}
    templates={templates.data}
    templatesState={stateOf(templates)}
    registeredTemplatesContent={tab === "registered" ? <RegisteredTemplates /> : undefined}
    policies={policies.data}
    policiesState={stateOf(policies)}
    campaigns={campaigns.data}
    campaignsState={stateOf(campaigns)}
    canManage={authorities.includes("NOTIFICATION_MANAGE")}
    unresolvedRetryId={pendingRetryId}
    notice={[notice, restorationNotice].filter(Boolean).join(" ")}
    recoveryError={recoveryError}
    onTabChange={(nextTab) => remember(nextTab, list.page, filters)}
    onSearch={(nextFilters) => { setSelectedId(pendingRetryId); remember("history", 0, nextFilters); }}
    onPageChange={(page) => { setSelectedId(pendingRetryId); remember("history", page, filters); }}
    onSelect={(id) => { setSelectedId(id); setNotice(""); setRecoveryError(""); }}
    detailHref={(id) => pathForAdminEntityScreen("26", id)}
    onOpenDetail={(id) => navigation.openDetail(pathForAdminEntityScreen("26", id), `notification:${id}:detail`)}
    onRetryQuery={retryQuery}
    onMutation={onMutation}
    onMutationComplete={onMutationComplete}
    onReconcileRetry={reconcileRetry}
  />;
}
